/**
 * Google reCAPTCHA v3 — token pentru API.
 * Site key: din `/api/recaptcha-site-key.php` (DB + admin), apoi REACT_APP_* / fallback Selena.
 */

import { resolveApiBaseUrl } from "@/utils/tracking";
import {
  RECAPTCHA_SITE_KEY_FALLBACK,
  isRecaptchaFallbackHost,
} from "@/config/recaptchaPublic";

function envSiteKey() {
  return typeof process !== "undefined" && process.env.REACT_APP_RECAPTCHA_SITE_KEY
    ? String(process.env.REACT_APP_RECAPTCHA_SITE_KEY).trim()
    : "";
}

/** Site key din env sau fallback hostname (fără încărcare remote). */
export function getRecaptchaSiteKey() {
  const fromEnv = envSiteKey();
  if (fromEnv) {
    return fromEnv;
  }
  if (typeof window !== "undefined" && isRecaptchaFallbackHost(window.location.hostname)) {
    return RECAPTCHA_SITE_KEY_FALLBACK;
  }
  return "";
}

/** localhost / 127.0.0.1 / ::1 — același contract ca skip captcha în PHP (public_captcha_should_skip_due_to_local_dev). */
function isBrowserLocalDevHost() {
  if (typeof window === "undefined") {
    return false;
  }
  const h = String(window.location.hostname || "").toLowerCase();
  return h === "localhost" || h === "127.0.0.1" || h === "[::1]" || h === "::1";
}

/** Dacă avem site key în bundle/fallback, încă încercăm captcha când API-ul public nu răspunde (evită 403 la booking). Pe localhost nu inferăm „server cere” dacă fetch-ul eșuează — altfel încărcăm cheia de producție și apare tooltip-ul Google „supported domains”. */
function captchaLikelyUsedOnServerFallback() {
  if (getRecaptchaSiteKey() === "") {
    return false;
  }
  return !isBrowserLocalDevHost();
}

/** Rezultat cache de la PHP după primul fetch. */
let cachedPublicRecaptcha = null;
let fetchPromise = null;

/** Reîncarcă meta cheie publică de la server (după schimbări în admin / evită cache inițial greșit). */
export function invalidateRecaptchaPublicConfig() {
  cachedPublicRecaptcha = null;
  fetchPromise = null;
}

/**
 * Încarcă config-ul public reCAPTCHA de pe server (site key + dacă serverul cere token).
 * Poate fi apelat din App la mount; dacă nu, prima cerere de token o declanșează.
 */
export function prefetchRecaptchaPublicConfig() {
  if (cachedPublicRecaptcha !== null) {
    return Promise.resolve(cachedPublicRecaptcha);
  }
  if (fetchPromise) {
    return fetchPromise;
  }
  fetchPromise = (async () => {
    const inferIfApiFails = captchaLikelyUsedOnServerFallback();
    try {
      const base = resolveApiBaseUrl({ forTracking: false }).replace(/\/+$/, "");
      const res = await fetch(`${base}/recaptcha-site-key.php`, {
        credentials: "same-origin",
        cache: "no-store",
      });
      const j = await res.json().catch(() => null);
      if (!j || j.ok !== true) {
        cachedPublicRecaptcha = {
          server_expects_captcha: inferIfApiFails,
          site_key: "",
          show_public_notice: false,
        };
        return cachedPublicRecaptcha;
      }
      cachedPublicRecaptcha = {
        server_expects_captcha: Boolean(j.server_expects_captcha),
        site_key: typeof j.site_key === "string" ? j.site_key.trim() : "",
        show_public_notice: Boolean(j.show_public_notice),
      };
      return cachedPublicRecaptcha;
    } catch {
      cachedPublicRecaptcha = {
        server_expects_captcha: inferIfApiFails,
        site_key: "",
        show_public_notice: false,
      };
      return cachedPublicRecaptcha;
    }
  })();
  return fetchPromise;
}

async function getPublicRecaptchaPayload() {
  if (cachedPublicRecaptcha !== null) {
    return cachedPublicRecaptcha;
  }
  return prefetchRecaptchaPublicConfig();
}

/** Bloc explicativ pe site (booking + VIN): activ doar dacă e bifat în admin și JSON-ul public îl include. */
export function shouldShowRecaptchaPublicNotice() {
  if (cachedPublicRecaptcha === null) {
    return false;
  }
  return Boolean(cachedPublicRecaptcha.show_public_notice);
}

export function isRecaptchaConfigured() {
  const local = getRecaptchaSiteKey() !== "";
  if (cachedPublicRecaptcha !== null) {
    /** Nu afișa bloc „reCAPTCHA activ” pe localhost când serverul nu aplică verificarea — altfel utilizatorul vede UI Google inutil. */
    if (isBrowserLocalDevHost() && !Boolean(cachedPublicRecaptcha.server_expects_captcha)) {
      return false;
    }
    const sk = typeof cachedPublicRecaptcha.site_key === "string" ? cachedPublicRecaptcha.site_key.trim() : "";
    return (
      (Boolean(cachedPublicRecaptcha.server_expects_captcha) || local || sk !== "") &&
      (sk !== "" || local)
    );
  }
  return local;
}

function loadRecaptchaScript(siteKey) {
  if (!siteKey) {
    return Promise.resolve();
  }
  if (typeof window === "undefined") {
    return Promise.resolve();
  }
  const w = window;
  let scriptEl = document.getElementById("recaptcha-v3-script");
  if (scriptEl) {
    const prev = scriptEl.getAttribute("src") || "";
    const encoded = encodeURIComponent(siteKey);
    if (!prev.includes(`render=${encoded}`)) {
      scriptEl.remove();
      scriptEl = null;
      try {
        delete w.grecaptcha;
      } catch {
        /* ignore */
      }
    }
  }
  if (w.grecaptcha) {
    return Promise.resolve();
  }
  scriptEl = document.getElementById("recaptcha-v3-script");
  if (scriptEl) {
    return new Promise((resolve, reject) => {
      const deadline = Date.now() + 15000;
      const tick = () => {
        if (w.grecaptcha) {
          resolve();
          return;
        }
        if (Date.now() > deadline) {
          reject(new Error("reCAPTCHA load timeout"));
          return;
        }
        requestAnimationFrame(tick);
      };
      tick();
    });
  }
  return new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.id = "recaptcha-v3-script";
    s.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("reCAPTCHA script failed to load"));
    document.head.appendChild(s);
  });
}

/** Mesaje tip „localhost nu e în lista de domenii” — afișare proeminentă în UI. */
export function isRecaptchaClientSetupErrorMessage(message) {
  if (typeof message !== "string") {
    return false;
  }
  const s = message.toLowerCase();
  return (
    (s.includes("localhost") && (s.includes("supported domain") || s.includes("list of supported"))) ||
    s.includes("not in the list of supported domains") ||
    s.includes("invalid site key") ||
    s.includes("error for site owner") ||
    (s.includes("127.0.0.1") && s.includes("domain"))
  );
}

async function waitForGrecaptchaClient(timeoutMs = 12000) {
  const w = window;
  const started = Date.now();
  while (Date.now() - started < timeoutMs) {
    const gc = w.grecaptcha;
    if (gc && typeof gc.ready === "function" && typeof gc.execute === "function") {
      return gc;
    }
    await new Promise((r) => setTimeout(r, 90));
  }
  return null;
}

/**
 * @param {"vin_decode"|"vin_parts"|"vin_products"|"booking_submit"} action
 * @returns {{ token: string, executeError: string | null }}
 */
export async function getRecaptchaTokenResult(action) {
  const remote = await getPublicRecaptchaPayload();
  const localKey = getRecaptchaSiteKey();
  const siteKeyFromRemote = typeof remote.site_key === "string" ? remote.site_key.trim() : "";
  const siteKey = siteKeyFromRemote || localKey;

  /**
   * Pe localhost, dacă API-ul spune că nu aplică captcha (Origin local), nu încărcăm scriptul Google.
   * Cheia din .env poate fi doar pentru producție → altfel apare badge/tooltip „Localhost is not in the list of supported domains”.
   */
  const skipGoogleOnLocalDev =
    isBrowserLocalDevHost() && !Boolean(remote.server_expects_captcha);

  /** Încercăm token dacă avem cheie și (serverul cere SAU avem cheie locală SAU API a trimis site key în DB). Evită 403 când prefetch-ul raportează greșit „nu cere”. */
  const shouldTryToken =
    siteKey !== "" &&
    !skipGoogleOnLocalDev &&
    (Boolean(remote.server_expects_captcha) || localKey !== "" || siteKeyFromRemote !== "");

  if (!shouldTryToken) {
    return { token: "", executeError: null };
  }

  try {
    await loadRecaptchaScript(siteKey);
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    return { token: "", executeError: msg || "reCAPTCHA script failed to load." };
  }
  const gc = await waitForGrecaptchaClient();
  if (!gc) {
    return {
      token: "",
      executeError:
        "reCAPTCHA nu s-a încărcat la timp. Verifică conexiunea, reîncarcă pagina sau permite încărcarea scripturilor de la google.com (uneori blocată de adblock / firewall).",
    };
  }
  await new Promise((resolve) => {
    gc.ready(resolve);
  });
  await new Promise((r) => setTimeout(r, 120));

  let executeError = null;
  for (let attempt = 0; attempt < 3; attempt++) {
    try {
      const token = await gc.execute(siteKey, { action });
      if (typeof token === "string" && token.length > 40) {
        return { token, executeError: null };
      }
    } catch (e) {
      executeError = e instanceof Error ? e.message : String(e);
    }
    await new Promise((r) => setTimeout(r, 280 * (attempt + 1)));
  }
  return { token: "", executeError };
}

/**
 * @param {"vin_decode"|"vin_parts"|"vin_products"|"booking_submit"} action
 * @returns {Promise<string>} jeton gol dacă serverul nu cere captcha sau lipsește cheia / scriptul
 */
export async function getRecaptchaToken(action) {
  const { token } = await getRecaptchaTokenResult(action);
  return token;
}

if (typeof window !== "undefined") {
  prefetchRecaptchaPublicConfig().catch(() => {});
}
